import { useEffect, useState } from "react";
import {
  ArrowRight,
  FolderKanban,
  Images,
  Inbox,
  Loader2,
  Mail,
  MessageSquare,
} from "lucide-react";
import {
  listSubmissions,
  listProgetti,
  listTestimonianze,
  listMedia,
} from "@/lib/api";

function parseDate(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).replace(" ", "T"));
  return isNaN(d) ? null : d;
}

function formatDay(iso) {
  const d = parseDate(iso);
  return d ? d.toLocaleDateString("it-IT") : "";
}

function isThisMonth(iso) {
  const d = parseDate(iso);
  if (!d) return false;
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

// Le liste possono arrivare come array o come { items: [...] }.
const asList = (v) => (Array.isArray(v) ? v : v?.items || []);

export default function DashboardPanel({ onNavigate }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    // Ogni sezione carica per conto suo: se un endpoint fallisce le altre card restano valide.
    Promise.allSettled([
      listSubmissions(),
      listProgetti(),
      listTestimonianze(),
      listMedia(),
    ]).then((results) => {
      if (cancelled) return;
      const [submissions, progetti, testimonianze, media] = results.map((r) =>
        r.status === "fulfilled" ? asList(r.value) : null
      );
      const failed = results.find((r) => r.status === "rejected");
      if (failed) setError(failed.reason?.message || "Errore di caricamento");
      setData({ submissions, progetti, testimonianze, media });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-7 h-7 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  const { submissions, progetti, testimonianze, media } = data;
  const count = (list) => (list ? list.length : "—");
  const unread = submissions ? submissions.filter((s) => !s.is_read).length : 0;
  const thisMonth = submissions
    ? submissions.filter((s) => isThisMonth(s.created_at)).length
    : 0;

  const stats = [
    {
      key: "richieste",
      label: "Richieste",
      icon: Inbox,
      value: count(submissions),
      hint: submissions ? `${thisMonth} questo mese` : null,
      badge: unread > 0 ? `${unread} da leggere` : null,
    },
    { key: "progetti", label: "Progetti", icon: FolderKanban, value: count(progetti) },
    { key: "testimonianze", label: "Testimonianze", icon: MessageSquare, value: count(testimonianze) },
    { key: "media", label: "Media", icon: Images, value: count(media), hint: "file caricati" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-3xl md:text-4xl tracking-tight font-semibold text-[#0B1E36]">
          Dashboard
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          Panoramica dei contenuti e delle richieste.
        </p>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onNavigate(s.key)}
              data-testid={`dashboard-stat-${s.key}`}
              className="group text-left bg-white border border-[#E2E8F0] rounded-lg hover:border-[#0B1E36] transition-colors p-6"
            >
              <div className="flex items-start justify-between mb-5">
                <span className="w-10 h-10 inline-flex items-center justify-center rounded-md bg-[#F1F5F9] text-[#0B1E36]">
                  <Icon className="w-5 h-5" />
                </span>
                <ArrowRight className="w-4 h-4 text-[#CBD5E1] group-hover:text-[#007AFF] transition-colors" />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-4xl tracking-tight font-semibold text-[#0B1E36]">
                  {s.value}
                </span>
                {s.badge && (
                  <span className="px-2 py-1 rounded bg-[#FF3B30] text-white text-[10px] uppercase tracking-[0.15em] font-bold">
                    {s.badge}
                  </span>
                )}
              </div>
              <div className="text-sm text-[#0B1E36] mt-1">{s.label}</div>
              {s.hint && <div className="text-xs text-[#64748B] mt-0.5">{s.hint}</div>}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ListCard title="Ultime richieste" action="Vedi tutte" onAction={() => onNavigate("richieste")}>
          {!submissions || submissions.length === 0 ? (
            <EmptyRow>Nessuna richiesta ricevuta.</EmptyRow>
          ) : (
            submissions.slice(0, 5).map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onNavigate("richieste")}
                className="w-full text-left flex items-center justify-between gap-4 px-6 py-4 hover:bg-[#F8FAFC] transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {!s.is_read && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] flex-shrink-0" />
                    )}
                    <span className="font-medium text-sm truncate">{s.nome}</span>
                  </div>
                  <div className="text-xs text-[#64748B] truncate inline-flex items-center gap-1.5 mt-0.5">
                    <Mail className="w-3 h-3" />
                    {s.email}
                  </div>
                </div>
                <span className="text-xs text-[#64748B] whitespace-nowrap">
                  {formatDay(s.created_at)}
                </span>
              </button>
            ))
          )}
        </ListCard>

        <ListCard title="Progetti" action="Vedi tutti" onAction={() => onNavigate("progetti")}>
          {!progetti || progetti.length === 0 ? (
            <EmptyRow>Nessun progetto inserito.</EmptyRow>
          ) : (
            progetti.slice(0, 5).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onNavigate("progetti")}
                className="w-full text-left flex items-center justify-between gap-4 px-6 py-4 hover:bg-[#F8FAFC] transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {p.cover_image_url ? (
                    <img
                      src={p.cover_image_url}
                      alt=""
                      className="w-10 h-10 object-cover rounded flex-shrink-0 bg-[#F1F5F9]"
                    />
                  ) : (
                    <span className="w-10 h-10 rounded bg-[#F1F5F9] flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{p.titolo}</div>
                    {p.categoria && (
                      <div className="text-xs text-[#64748B] truncate mt-0.5">
                        {p.categoria}
                      </div>
                    )}
                  </div>
                </div>
                {p.data_installazione && (
                  <span className="text-xs text-[#64748B] whitespace-nowrap">
                    {formatDay(p.data_installazione)}
                  </span>
                )}
              </button>
            ))
          )}
        </ListCard>
      </div>
    </div>
  );
}

function ListCard({ title, action, onAction, children }) {
  return (
    <section className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]">
        <h2 className="text-xs uppercase tracking-[0.15em] font-bold text-[#0B1E36]">{title}</h2>
        <button
          type="button"
          onClick={onAction}
          className="text-xs font-bold text-[#007AFF] hover:underline"
        >
          {action}
        </button>
      </div>
      <div className="divide-y divide-[#E2E8F0]">{children}</div>
    </section>
  );
}

function EmptyRow({ children }) {
  return <div className="px-6 py-8 text-sm text-[#64748B] text-center">{children}</div>;
}
